import { useEffect, useState, type FormEvent } from "react";
import * as endpoints from "@/api/endpoints";
import { apiErrorCode, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { PlanChangeDialog } from "@/components/PlanChangeDialog";

export function AccountPage() {
  const { user } = useAuth();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [planChanged, setPlanChanged] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPw.length < 8) {
      setError("新しいパスワードは 8 文字以上で入力してください。");
      return;
    }
    if (newPw !== confirmPw) {
      setError("新しいパスワードと確認用パスワードが一致しません。");
      return;
    }
    if (newPw === currentPw) {
      setError("現在のパスワードと異なるものを設定してください。");
      return;
    }

    setSubmitting(true);
    try {
      await endpoints.changePassword(currentPw, newPw);
      setSuccess("パスワードを変更しました。");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "INVALID_CREDENTIALS") {
        setError("現在のパスワードが正しくありません。");
      } else {
        setError(apiErrorMessage(err, "パスワード変更に失敗しました"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-semibold">アカウント設定</h1>

      <div className="card p-4">
        <h2 className="font-semibold mb-3">企業情報</h2>
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <dt className="text-gray-500">企業名</dt>
          <dd className="col-span-2">{user?.tenant?.name ?? "-"}</dd>
          <dt className="text-gray-500">プラン</dt>
          <dd className="col-span-2 flex items-center gap-3">
            <span className="uppercase font-medium">{user?.tenant?.plan ?? "-"}</span>
            <button
              type="button"
              className="text-brand-700 hover:underline text-xs"
              onClick={() => setShowPlanDialog(true)}
            >
              プラン変更
            </button>
          </dd>
          <dt className="text-gray-500">メールアドレス</dt>
          <dd className="col-span-2">{user?.email ?? "-"}</dd>
        </dl>
        {planChanged && (
          <div className="mt-3 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
            {planChanged}
          </div>
        )}
        <div className="mt-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
          <div className="font-semibold">配送先: 株式会社 LOGEX 宛</div>
          <div className="text-xs mt-1 text-blue-800">
            すべての発注は弊社指定の物流拠点（株式会社 LOGEX）で一括受取となります。
            個別の発送先指定はご利用いただけません。
          </div>
        </div>
      </div>

      {showPlanDialog && (
        <PlanChangeDialog
          onClose={() => setShowPlanDialog(false)}
          onChanged={(res) => {
            const msg =
              res.change_type === "upgrade"
                ? `${res.previous_plan.toUpperCase()} → ${res.plan.toUpperCase()} へアップグレードしました。上位 tier の商品がカタログに表示されます。`
                : `${res.previous_plan.toUpperCase()} → ${res.plan.toUpperCase()} へダウングレードしました。`;
            setPlanChanged(msg);
          }}
        />
      )}

      <form onSubmit={onSubmit} className="card p-4 space-y-4">
        <h2 className="font-semibold">パスワード変更</h2>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        )}

        <div>
          <label className="label" htmlFor="current_password">
            現在のパスワード
          </label>
          <input
            id="current_password"
            type="password"
            className="input"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            required
            autoComplete="current-password"
            disabled={submitting}
          />
        </div>
        <div>
          <label className="label" htmlFor="new_password">
            新しいパスワード（8 文字以上）
          </label>
          <input
            id="new_password"
            type="password"
            className="input"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>
        <div>
          <label className="label" htmlFor="confirm_password">
            新しいパスワード（確認用）
          </label>
          <input
            id="confirm_password"
            type="password"
            className="input"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "変更中..." : "パスワードを変更"}
        </button>
      </form>

      <SubscriptionSection />
      <CancellationSection />
    </div>
  );
}

// ============================================================
// サブスクリプション状態セクション
// ============================================================

const SUB_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  trialing: { label: "トライアル中", cls: "badge-blue" },
  active: { label: "利用中", cls: "badge-green" },
  past_due: { label: "支払い失敗", cls: "badge-red" },
  canceled: { label: "解約済み", cls: "badge-gray" },
  unpaid: { label: "未払い", cls: "badge-red" },
  incomplete: { label: "支払い未完了", cls: "badge-yellow" },
  incomplete_expired: { label: "期限切れ", cls: "badge-gray" },
};

function formatUnixDate(ts: number | null): string {
  if (!ts) return "-";
  return new Date(ts * 1000).toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysUntil(ts: number | null): number | null {
  if (!ts) return null;
  const diffMs = ts * 1000 - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

function SubscriptionSection() {
  const [status, setStatus] = useState<endpoints.SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.fetchSubscription();
      setStatus(res);
    } catch (e) {
      setError(apiErrorMessage(e, "サブスクリプション情報の取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const onOpenPortal = async () => {
    setOpeningPortal(true);
    setFlash(null);
    try {
      const returnUrl = window.location.origin + "/account";
      const res = await endpoints.createBillingPortalSession(returnUrl);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "ポータルの起動に失敗しました") });
    } finally {
      setOpeningPortal(false);
    }
  };

  const onCancelPending = async () => {
    if (!window.confirm("プラン変更の予約を取り消しますか？")) return;
    setCancellingPending(true);
    setFlash(null);
    try {
      await endpoints.cancelPendingPlanChange();
      setFlash({ type: "ok", text: "プラン変更の予約を取り消しました" });
      await reload();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "取り消しに失敗しました") });
    } finally {
      setCancellingPending(false);
    }
  };

  if (loading) {
    return <div className="card p-4 text-sm text-gray-500">読み込み中...</div>;
  }
  if (error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );
  }
  if (!status) return null;

  const subStatusInfo =
    status.status && SUB_STATUS_LABEL[status.status]
      ? SUB_STATUS_LABEL[status.status]
      : null;

  const trialDaysLeft = daysUntil(status.trial_end);
  const isTrialing = status.status === "trialing";
  const hasSubscription = !!status.stripe_subscription_id;

  return (
    <div className="card p-4">
      <h2 className="font-semibold mb-3">サブスクリプション</h2>

      {flash && (
        <div
          className={`rounded-md px-3 py-2 text-sm border mb-3 ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {!hasSubscription ? (
        <p className="text-sm text-gray-600">
          サブスクリプション情報は未登録です。お問い合わせください。
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-3 gap-2 text-sm mb-3">
            <dt className="text-gray-500">プラン</dt>
            <dd className="col-span-2 uppercase font-medium">{status.plan}</dd>

            <dt className="text-gray-500">状態</dt>
            <dd className="col-span-2">
              {subStatusInfo ? (
                <span className={subStatusInfo.cls}>{subStatusInfo.label}</span>
              ) : (
                status.status || "-"
              )}
            </dd>

            {isTrialing && status.trial_end && (
              <>
                <dt className="text-gray-500">トライアル終了</dt>
                <dd className="col-span-2">
                  {formatUnixDate(status.trial_end)}
                  {trialDaysLeft !== null && (
                    <span className="ml-2 text-xs text-brand-700 font-medium">
                      （残り {trialDaysLeft} 日）
                    </span>
                  )}
                </dd>
              </>
            )}

            {!isTrialing && status.current_period_end && (
              <>
                <dt className="text-gray-500">次回請求日</dt>
                <dd className="col-span-2">{formatUnixDate(status.current_period_end)}</dd>
              </>
            )}
          </dl>

          {isTrialing && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 mb-3">
              ℹ️ 現在トライアル期間中です。トライアル終了までにお支払い方法（カード情報）をご登録ください。
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void onOpenPortal()}
              disabled={openingPortal}
            >
              {openingPortal ? "起動中..." : "カード登録・請求履歴（Stripe ポータル）"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void reload()}
            >
              再読込
            </button>
          </div>
        </>
      )}

      <PendingPlanChangeBanner
        onCancel={onCancelPending}
        cancelling={cancellingPending}
        refreshSubscription={reload}
      />
    </div>
  );
}

/**
 * ダウン予約のバナー。user.tenant から取得する。
 */
function PendingPlanChangeBanner({
  onCancel,
  cancelling,
  refreshSubscription: _refreshSubscription,
}: {
  onCancel: () => Promise<void>;
  cancelling: boolean;
  refreshSubscription: () => Promise<void>;
}) {
  const { user } = useAuth();
  const tenant = user?.tenant as
    | {
        pending_plan_change?: string | null;
        pending_plan_change_effective_at?: string | null;
      }
    | undefined;
  if (!tenant?.pending_plan_change) return null;

  const effective = tenant.pending_plan_change_effective_at
    ? new Date(tenant.pending_plan_change_effective_at).toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : "次回請求日";

  return (
    <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-900">
      <div className="font-semibold mb-1">プラン変更の予約があります</div>
      <div className="text-xs">
        {effective} から{" "}
        <span className="uppercase font-semibold">{tenant.pending_plan_change}</span>{" "}
        に変更される予定です。
      </div>
      <button
        type="button"
        className="btn-secondary mt-2 px-2 py-1 text-xs"
        onClick={() => void onCancel()}
        disabled={cancelling}
      >
        {cancelling ? "処理中..." : "予約を取り消す"}
      </button>
    </div>
  );
}

// ============================================================
// 退会セクション
// ============================================================

function CancellationSection() {
  const [status, setStatus] = useState<endpoints.CancellationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await endpoints.fetchCancellationStatus();
      setStatus(res);
    } catch (e) {
      setLoadError(apiErrorMessage(e, "退会状況の取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  };

  // 初回のみ取得
  useEffect(() => {
    void load();
  }, []);

  const onSubmitRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!acknowledged) return;
    setSubmitting(true);
    setFlash(null);
    try {
      const res = await endpoints.requestCancellation(reason.trim() || undefined);
      setStatus(res);
      setShowDialog(false);
      setReason("");
      setAcknowledged(false);
      setFlash({
        type: "ok",
        text: "退会申請を受け付けました。担当者よりご連絡いたします。",
      });
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "退会申請に失敗しました") });
    } finally {
      setSubmitting(false);
    }
  };

  const onWithdraw = async () => {
    if (!window.confirm("退会申請を取り消します。よろしいですか？")) return;
    setSubmitting(true);
    setFlash(null);
    try {
      const res = await endpoints.withdrawCancellation();
      setStatus(res);
      setFlash({ type: "ok", text: "退会申請を取り消しました。" });
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "取り消しに失敗しました") });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-4 text-sm text-gray-500">読み込み中...</div>
    );
  }
  if (loadError) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
        {loadError}
      </div>
    );
  }

  const requested = status?.cancellation_requested_at;
  const finalized = status?.cancellation_finalized_at;

  return (
    <div className="card p-4 border-gray-200">
      <h2 className="font-semibold mb-2">退会</h2>

      {flash && (
        <div
          className={`rounded-md px-3 py-2 text-sm border mb-3 ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {finalized ? (
        <div className="text-sm text-gray-600">
          退会が完了しています（{new Date(finalized).toLocaleString("ja-JP")}）。
        </div>
      ) : requested ? (
        <div className="space-y-3">
          <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
            退会申請を受け付けています（{new Date(requested).toLocaleString("ja-JP")}）。
            弊社にて内容を確認の上、担当者よりご連絡いたします。
            確定までの間は通常どおりご利用いただけます。
          </div>
          {status?.cancellation_reason && (
            <div className="text-sm">
              <div className="text-gray-500 text-xs">申請時の理由</div>
              <div className="whitespace-pre-wrap">{status.cancellation_reason}</div>
            </div>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void onWithdraw()}
            disabled={submitting}
          >
            {submitting ? "処理中..." : "退会申請を取り消す"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            サービスの利用を終了される場合は、退会申請を送信してください。
            申請後、弊社にて内容を確認し退会手続きを完了いたします。
          </p>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setShowDialog(true)}
          >
            退会申請する
          </button>
        </div>
      )}

      {showDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b">
              <h3 className="font-semibold">退会申請</h3>
            </div>
            <form onSubmit={onSubmitRequest} className="p-4 space-y-3">
              <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
                <strong>退会にあたっての注意点</strong>
                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                  <li>退会申請は弊社での承認後に確定します</li>
                  <li>承認までは通常どおりサービスをご利用いただけます</li>
                  <li>未処理の発注がある場合は担当者よりご連絡します</li>
                  <li>確定後、サブスクリプションの課金は停止されます</li>
                  <li>確定後はログインできなくなります</li>
                </ul>
              </div>
              <div>
                <label className="label">退会理由（任意）</label>
                <textarea
                  className="input h-24 resize-none"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="差し支えなければご記入ください。サービス改善の参考にいたします。"
                  disabled={submitting}
                  maxLength={2000}
                />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  disabled={submitting}
                />
                <span>
                  上記の注意点を確認し、退会申請を送信することに同意します。
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowDialog(false);
                    setReason("");
                    setAcknowledged(false);
                  }}
                  disabled={submitting}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="btn-danger"
                  disabled={submitting || !acknowledged}
                >
                  {submitting ? "送信中..." : "退会申請を送信"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
